/*
 * One ask for notifications (Saif, 25 Oct 2026; CLAUDE.md §7.7). Our own screen, whose wording names offers, comes
 * before the phone's Allow box. Turn on registers the device with the wording shown, so the server records order
 * updates AND offers by push; Not now records nothing and never opens the phone's box. When it shows is the shared rule
 * (packages/shared/src/marketing/notifications.ts); this file holds the app's side: what each button does, and the
 * two things kept in memory for this visit (just joined, already shown).
 */
import { consentWording, DEFAULT_NOTIFICATIONS_ASK, notificationsAsk, type NotificationPermission, type NotificationsAskKind, type NotificationsAskSettings } from '@tikkas/shared';

export type { NotificationsAskKind };

/** The ask's settings as the server sent them, the starting ones when an older server sent none. */
export function askSettings(cfg: { ask?: Partial<NotificationsAskSettings> } | undefined): NotificationsAskSettings {
  return { ...DEFAULT_NOTIFICATIONS_ASK, ...(cfg?.ask ?? {}) };
}

/* ---------- this visit only (never stored: a cold start begins again) ---------- */

let justJoined = false;
let shown = false;
/** Joining finished (the name sheet is done, or there was none): the first Home asks. */
export function markJustJoined(): void {
  justJoined = true;
}
export const isJustJoined = (): boolean => justJoined;
export function markAskShown(): void {
  shown = true;
  justJoined = false;
}
export const askShownThisVisit = (): boolean => shown;
/** For the tests. */
export function resetAskVisit(): void {
  justJoined = false;
  shown = false;
}

/* ---------- when ---------- */

export interface AskContext {
  enabled: boolean;
  /** Push can work here at all: the iPhone app, or a website whose server has web push keys. */
  available: boolean;
  signedIn: boolean;
  permission: NotificationPermission;
  subscribed: boolean;
  place: 'home' | 'status';
  offersDecided: boolean;
  notNowAt: string | null;
  askAgainDays: number;
  now: Date;
}

/** Which screen to show now (the shared rule, plus the visit's memory). */
export function askToShow(c: AskContext): NotificationsAskKind | null {
  if (!c.available) return null;
  return notificationsAsk({ enabled: c.enabled, signedIn: c.signedIn, permission: c.permission, place: c.place, justJoined, subscribed: c.subscribed, offersDecided: c.offersDecided, notNowAt: c.notNowAt, askAgainDays: c.askAgainDays, now: c.now, shownThisVisit: shown });
}

/* ---------- what the screen says and what each button does ---------- */

export type AskAction =
  /** Register the device (opens the phone's Allow box if undecided) with the consent block. */
  | { call: 'subscribe'; consent: { wording: string; offers: boolean } }
  /** Already allowed: record the offers choice with the wording shown. */
  | { call: 'choice'; consent: { wording: string; offers: boolean } }
  /** Not now: tell the server when, record no consent, never touch the phone's box. */
  | { call: 'not_now' };

export interface AskScreen {
  title: string;
  text: string;
  primary: { label: string; action: AskAction };
  secondary: { label: string; action: AskAction };
}

export function askScreen(kind: NotificationsAskKind, s: NotificationsAskSettings): AskScreen {
  if (kind === 'offers') {
    return {
      title: s.offersTitle,
      text: s.text,
      primary: { label: s.offersYes, action: { call: 'choice', consent: { wording: consentWording(s.offersTitle, s.text, s.offersYes), offers: true } } },
      secondary: { label: s.offersNo, action: { call: 'choice', consent: { wording: consentWording(s.offersTitle, s.text, s.offersNo), offers: false } } },
    };
  }
  return {
    title: s.title,
    text: s.text,
    primary: { label: s.turnOn, action: { call: 'subscribe', consent: { wording: consentWording(s.title, s.text, s.turnOn), offers: true } } },
    secondary: { label: s.notNow, action: { call: 'not_now' } },
  };
}

/** Closing the screen without a button (tap outside, swipe down) is a Not now. */
export const DISMISS: AskAction = { call: 'not_now' };

/** What to say after Turn on, by what the phone answered. Don't Allow records nothing. */
export function subscribeToast(r: 'subscribed' | 'denied' | 'unsupported' | 'not_configured' | 'failed'): string | null {
  if (r === 'subscribed') return 'Notifications are on';
  if (r === 'denied') return 'No problem. You can turn them on in your phone’s Settings later.';
  if (r === 'not_configured' || r === 'unsupported') return 'Notifications aren’t available here yet';
  return 'Couldn’t turn notifications on. Try again in Account.';
}
